<template>
  <master-layout v-bind:pageTitle="$t('documentation')">
    <div class="ion-padding">
      <div class="doc-section-tabs">
        <button
          v-for="section in sections"
          :key="section.key"
          :class="['doc-tab', { 'doc-tab--active': section.key === activeSectionKey }]"
          @click="selectSection(section.key)"
        >
          {{ $t(section.titleKey) }}
        </button>
      </div>

      <div class="doc-content" v-if="loading">
        <p>{{ $t('loading') }}</p>
      </div>
      <div class="doc-content" v-else-if="error">
        <p>{{ error }}</p>
      </div>
      <div class="doc-content" v-else>
        <div class="doc-text" v-html="content"></div>
      </div>
    </div>
  </master-layout>
</template>

<script>
import { defineComponent, getCurrentInstance, ref, computed, watch, onMounted } from 'vue';
import MasterLayout from "../components/MasterLayout.vue";

export default defineComponent({
  name: 'Documentation',
  components: {
    MasterLayout,
  },
  setup() {
    const { proxy } = getCurrentInstance();
    const sections = [
      { key: 'basic-concepts', titleKey: 'doc.basicConcepts' },
      { key: 'tunes-and-scores', titleKey: 'doc.tunesAndScores' },
      { key: 'tunebooks', titleKey: 'doc.tunebooks' },
      { key: 'musicians', titleKey: 'doc.musicians' },
      { key: 'users', titleKey: 'doc.users' },
      { key: 'places', titleKey: 'doc.places' },
      { key: 'groups', titleKey: 'doc.groups' },
      { key: 'playlists', titleKey: 'doc.playlists' },
      { key: 'sessions', titleKey: 'doc.sessions' },
      { key: 'abc-notation', titleKey: 'doc.abcNotation' },
      { key: 'abc-infofields', titleKey: 'doc.abcInfofields' },
    ];

    const activeSectionKey = ref(sections[0].key);
    const content = ref('');
    const loading = ref(false);
    const error = ref('');

    const i18n = proxy && proxy.$i18n ? proxy.$i18n : null;
    const currentLocale = computed(() => (i18n ? i18n.locale : 'en'));

    const buildDocUrl = (locale, sectionKey) => {
      const lang = locale || 'en';
      return `/docs/${lang}/${sectionKey}.txt`;
    };

    const loadSection = async () => {
      loading.value = true;
      error.value = '';
      content.value = '';

      const locale = currentLocale.value || 'en';
      const primaryUrl = buildDocUrl(locale, activeSectionKey.value);
      const fallbackUrl = locale === 'en' ? null : buildDocUrl('en', activeSectionKey.value);

      try {
        let response = await fetch(primaryUrl);
        if (!response.ok && fallbackUrl) {
          response = await fetch(fallbackUrl);
        }
        if (!response.ok) {
          throw new Error('Failed to load documentation');
        }
        const text = await response.text();
        content.value = text;
      } catch (e) {
        console.error(e);
        error.value = 'Unable to load documentation text.';
      } finally {
        loading.value = false;
      }
    };

    const selectSection = (key) => {
      if (activeSectionKey.value !== key) {
        activeSectionKey.value = key;
        loadSection();
      }
    };

    watch(currentLocale, () => {
      loadSection();
    });

    onMounted(() => {
      loadSection();
    });

    return {
      sections,
      activeSectionKey,
      content,
      loading,
      error,
      selectSection,
    };
  },
});
</script>

<style>
.doc-section-tabs {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding-bottom: 0.25rem;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; /* Firefox */
}

.doc-section-tabs::-webkit-scrollbar {
  display: none; /* Chrome/Safari */
}

.doc-tab {
  padding: 0.4rem 0.8rem;
  border-radius: 16px;
  border: 1px solid var(--ion-color-medium);
  background: var(--ion-color-light);
  font-size: 0.9rem;
  flex-shrink: 0;
  white-space: nowrap;
}

.doc-tab--active {
  background: var(--ion-color-primary);
  color: white;
  border-color: var(--ion-color-primary);
}

.doc-content {
  white-space: pre-wrap;
}

.doc-text {
  white-space: pre-wrap;
  font-family: inherit;
}

.doc-text img {
  max-width: none !important;
  width: auto !important;
  height: auto !important;
  display: inline-block;
}
</style>
